import { EXPECTED_PERMANENT_EVENTS, PLAN_AHEAD_BARS } from "../../audio/constants";
import type { EngineSnapshot } from "../../state/types";
import { Panel } from "../ui/Panel";

export interface DiagnosticsProps {
  snapshot: EngineSnapshot;
}

interface Row {
  label: string;
  value: string;
  /** Expected behaviour, shown so a reader can judge the value without guessing. */
  expected: string;
  warn: boolean;
}

function seconds(value: number | null): string {
  if (value === null) return "非対応";
  return `${(value * 1000).toFixed(1)} ms`;
}

/**
 * "scheduled event count" is deliberately split.
 *
 * A constant number of permanent transport callbacks says nothing about whether
 * future note reservations are piling up, so the queue depth, planned bars and
 * cumulative dispatch total are reported separately, each with its expected
 * behaviour (docs/implementation-plan.md ch.14).
 */
export function Diagnostics({ snapshot }: DiagnosticsProps) {
  const d = snapshot.diagnostics;
  const alive = snapshot.status !== "idle" && snapshot.status !== "disposed";

  const rows: Row[] = [
    {
      label: "AudioContext state",
      value: d.audioContextState,
      expected: "running",
      warn: alive && d.audioContextState !== "running",
    },
    {
      label: "Base latency",
      value: seconds(d.baseLatency),
      expected: "参照値",
      warn: false,
    },
    {
      label: "Output latency",
      value: seconds(d.outputLatency),
      expected: "参照値（非対応あり）",
      warn: false,
    },
    {
      label: "Look ahead",
      value: seconds(d.lookAhead),
      expected: "参照値",
      warn: false,
    },
    {
      label: "transportCallbackCount",
      value: String(d.transportCallbackCount),
      expected: `常に ${EXPECTED_PERMANENT_EVENTS}`,
      warn: alive && d.transportCallbackCount !== EXPECTED_PERMANENT_EVENTS,
    },
    {
      label: "plannedBarCount",
      value: String(d.plannedBarCount),
      expected: `1〜${PLAN_AHEAD_BARS}`,
      warn: snapshot.status === "running" && d.plannedBarCount > PLAN_AHEAD_BARS,
    },
    {
      label: "pendingDispatchEventCount",
      value: String(d.pendingDispatchEventCount),
      expected: "有界（単調増加は異常）",
      warn: d.pendingDispatchEventCount > 64,
    },
    {
      label: "activeVoiceCount",
      value: String(d.activeVoiceCount),
      expected: "有界",
      warn: d.activeVoiceCount > 32,
    },
    {
      label: "nodeCountEstimate",
      value: String(d.nodeCountEstimate),
      expected: "init後は一定",
      warn: false,
    },
    {
      label: "dispatchedEventTotal",
      value: String(d.dispatchedEventTotal),
      expected: "単調増加が正常",
      warn: false,
    },
    {
      label: "lateDropCount",
      value: String(d.lateDropCount),
      expected: "0",
      warn: d.lateDropCount > 0,
    },
    {
      label: "master gain / reason",
      value: `${d.masterAppliedGain.toFixed(3)} / ${d.masterMuteReason}`,
      expected: "running時のみ >0",
      warn: snapshot.status === "running" && d.masterMuteReason === "paused",
    },
    {
      label: "resumeDrainRemaining",
      value: `${d.resumeDrainRemainingMs.toFixed(0)} ms`,
      expected: "Resume待機中のみ >0",
      warn: false,
    },
    {
      label: "dispatchTickHz",
      value: d.dispatchTickHz.toFixed(1),
      expected: "BPMから予測される値の±20%",
      warn: false,
    },
    {
      label: "UI FPS",
      value: d.uiFps.toFixed(0),
      expected: "30以上（非表示時は0）",
      // requestAnimationFrame is throttled while the tab is hidden, so a low
      // value there is expected rather than a problem worth flagging.
      warn: alive && d.uiFps < 30 && document.visibilityState === "visible",
    },
    { label: "Seed", value: snapshot.seed, expected: "Reset後も同一", warn: false },
    {
      label: "Last error",
      value: d.lastError ?? "なし",
      expected: "なし",
      warn: d.lastError !== null,
    },

    // --- Phase 1A music diagnostics ---
    {
      label: "genre normalized",
      value: `A ${d.genreNormalized.ambient.toFixed(2)} / T ${d.genreNormalized.techno.toFixed(2)} / J ${d.genreNormalized.jazz.toFixed(2)}`,
      expected: "合計 1.00",
      warn:
        Math.abs(
          d.genreNormalized.ambient + d.genreNormalized.techno + d.genreNormalized.jazz - 1,
        ) > 0.01,
    },
    {
      label: "current energy",
      value: d.currentEnergy.toFixed(3),
      expected: "スライダー値へ追従",
      warn: false,
    },
    {
      label: "current density",
      value: d.currentDensity.toFixed(3),
      expected: "スライダー値へ追従",
      warn: false,
    },
    {
      label: "current section",
      value: d.currentSection,
      expected: "固定周期で切り替わらない",
      warn: false,
    },
    {
      label: "active rhythm roles",
      value: d.activeRhythmRoles.length > 0 ? d.activeRhythmRoles.join(", ") : "（なし）",
      expected: "常に全役割ではない",
      warn: alive && d.activeRhythmRoles.length === 4,
    },
    {
      label: "events this bar",
      value: String(d.eventsThisBar),
      expected: "Densityに追従、上限内",
      warn: d.eventsThisBar > 64,
    },
    {
      label: "average velocity",
      value: d.averageVelocity.toFixed(3),
      expected: "Energyに追従",
      warn: false,
    },
    {
      label: "average timing offset",
      value: `${d.averageTimingOffsetMs.toFixed(1)} ms`,
      expected: "±60ms以内",
      warn: Math.abs(d.averageTimingOffsetMs) > 60,
    },
    {
      label: "rhythm pattern hash",
      value: d.rhythmPatternHash,
      expected: "同じ値が続かない",
      warn: false,
    },
  ];

  return (
    <Panel
      title="Diagnostics"
      summary={
        <span className="panel__badge">
          callbacks {d.transportCallbackCount} / pending {d.pendingDispatchEventCount} / late{" "}
          {d.lateDropCount}
        </span>
      }
    >
      <table className="diagnostics">
        <caption className="visually-hidden">
          Audio engine diagnostics with expected behaviour for each metric
        </caption>
        <thead>
          <tr>
            <th scope="col">指標</th>
            <th scope="col">値</th>
            <th scope="col">期待</th>
            <th scope="col">判定</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className={row.warn ? "diagnostics__row--warn" : undefined}>
              <th scope="row">{row.label}</th>
              <td className="diagnostics__value">{row.value}</td>
              <td className="diagnostics__expected">{row.expected}</td>
              {/* Text, not colour alone. */}
              <td>{row.warn ? "警告" : "OK"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}
